<script lang="ts">
const __DsBlock = {
	icon: 'lucide:text-cursor-input',
	name: '$t:blocks.input_decimal_number.name',
	description: '$t:blocks.input_decimal_number.description',
	type: 'input',
	category: 'input',
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => isPrimitiveTypeDecimal(primitiveType) ? ['number'] : [],
	},
	options: [
		{ name: 'modelValue', label: '$t:blocks.common.props.value', model: true },
		{ name: 'min', label: '$t:blocks.common.props.min' },
		{ name: 'max', label: '$t:blocks.common.props.max' },
		{ name: 'step', label: '$t:blocks.common.props.step' },
		{ name: 'placeholder', label: '$t:blocks.common.props.placeholder' },
		{ name: 'variant', label: '$t:blocks.common.props.variant', type: 'enum', values: BLOCK_INPUT_VARIANT_OPTIONS },
		{ name: 'size', label: '$t:blocks.common.props.size', type: 'enum', values: BLOCK_CONTROL_SIZE_OPTIONS },
		{ name: 'block', label: '$t:blocks.common.props.block' },
		{ name: 'disabled', label: '$t:blocks.common.props.disabled' },
		{ name: 'readonly', label: '$t:blocks.common.props.readonly' },
		{ name: 'disabledNotice', label: '$t:blocks.common.props.disabled_notice' },
	],
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { Decimal } from '#layers/data-model/public';
import type { InputDecimalNumberProps } from '#layers/ui/public';
import { computed } from 'vue';
import { isNil } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { getDecimalConstraints, isPrimitiveTypeDecimal, toDecimal } from '#layers/data-model/public';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends
	InputBlockProps,
	Pick<InputDecimalNumberProps, 'placeholder' | 'variant' | 'size' | 'min' | 'max' | 'step' | 'precision' | 'scale'> {
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), { block: true });
const model = defineModel<Decimal | null>();

;

const forwarded = computed(() => ({
	placeholder: props.placeholder,
	variant: props.variant,
	size: props.size,
	min: props.min?.toString(),
	max: props.max?.toString(),
	step: props.step?.toString(),
	readonly: props.readonly,
}));
const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(props, { model });
const inputValue = computed({
	get: () => modelValue.value,
	set: (value: string | null) => {
		modelValue.value = value === null ? null : toDecimal(value);
	},
});
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const constraints = computed(() => getDecimalConstraints(field.value?.type));

const precision = computed(() => {
	const fieldPrecision = constraints.value.precision;

	if (!isNil(props.precision) && !isNil(fieldPrecision)) {
		return Math.min(props.precision, fieldPrecision);
	}

	return fieldPrecision ?? props.precision;
});

const scale = computed(() => {
	const fieldScale = constraints.value.scale;

	if (!isNil(props.scale) && !isNil(fieldScale)) {
		return Math.min(props.scale, fieldScale);
	}

	return fieldScale ?? props.scale;
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- `block` widens, it must not grow: the form's control column is a vertical flex box, so
			`flex-1` would stretch the root over a taller row and fling the absolute steppers apart. -->
		<DInputDecimalNumber
			v-bind="forwarded"
			v-model="inputValue"
			:precision
			:scale
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full': block }"
		/>
	</DTooltip>
</template>
